import { useEffect, useRef } from "react";
import { Search, X } from "lucide-react";

/**
 * Google-style live search input.
 *
 * Filters happen at the callsite: the parent maintains the `query` state and
 * uses it to filter its own list on every render. This component only handles
 * the visual input + clear button + optional focus shortcut ("/").
 *
 * Props
 * -----
 * value:       current query string
 * onChange:    (nextValue: string) => void
 * placeholder: e.g. "Buscar empleado, obra, vehículo…"
 * testId:      data-testid for the input
 * hotkey:      if true, pressing "/" anywhere focuses the input (default true)
 */
export default function LiveSearch({
  value,
  onChange,
  placeholder = "Buscar…",
  testId = "live-search",
  hotkey = true,
  className = "",
}) {
  const ref = useRef(null);

  useEffect(() => {
    if (!hotkey) return;
    const onKey = (e) => {
      if (
        e.key === "/" &&
        !e.metaKey && !e.ctrlKey && !e.altKey &&
        document.activeElement?.tagName !== "INPUT" &&
        document.activeElement?.tagName !== "TEXTAREA"
      ) {
        e.preventDefault();
        ref.current?.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [hotkey]);

  return (
    <div className={`relative ${className}`}>
      <Search
        size={16}
        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none"
      />
      <input
        ref={ref}
        data-testid={testId}
        type="search"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="brutal-input w-full pl-9 pr-9"
      />
      {value ? (
        <button
          type="button"
          data-testid={`${testId}-clear`}
          onClick={() => onChange("")}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 rounded-md text-slate-400 hover:text-rose-600 hover:bg-slate-100 inline-flex items-center justify-center"
          title="Limpiar búsqueda"
        >
          <X size={14} />
        </button>
      ) : (
        <span className="hidden sm:inline-flex absolute right-2 top-1/2 -translate-y-1/2 h-6 px-1.5 rounded border border-slate-200 bg-slate-50 text-[10px] font-mono text-slate-400 items-center">
          /
        </span>
      )}
    </div>
  );
}

/**
 * Helper: match a text query against a list of fields, case- and
 * diacritic-insensitive. Returns true if ANY field contains the query.
 */
const _fold = (s) =>
  String(s ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

export function matchesQuery(query, ...fields) {
  const q = _fold(query).trim();
  if (!q) return true;
  const tokens = q.split(/\s+/);
  const hay = fields.map(_fold).join(" ");
  return tokens.every((t) => hay.includes(t));
}
